/**
 * Mapbox Directions — recommended driving path between waypoints.
 *
 * Tour maps used to draw a geodesic (straight) line between stops. That is
 * not how anyone drives Ruta de las Flores. Mapbox already supplies the
 * Directions REST API behind the same public token as geocoding; we do not
 * add a second map SDK.
 *
 * The polyline is cached in memory for the session. Failures are not cached
 * so a later remount can retry. A consumer can still show the itinerary's
 * stop markers, but must never manufacture a straight route from a null
 * result.
 */
import { env } from '../env'
import { fetchWithTimeout } from '../utils/fetch-with-timeout'

export type LngLat = [number, number]

const DIRECTIONS_URL = 'https://api.mapbox.com/directions/v5/mapbox/driving'
const DIRECTIONS_TIMEOUT_MS = 8_000
/** Mapbox Directions v5 accepts at most 25 coordinates per request. */
const MAX_WAYPOINTS = 25

const polylineCache = new Map<string, GeoJSON.Feature<GeoJSON.LineString>>()
const inflight = new Map<string, Promise<GeoJSON.Feature<GeoJSON.LineString> | null>>()

export function drivingRouteCacheKey(coordinates: readonly LngLat[]): string {
  return coordinates.map(([lng, lat]) => `${lng.toFixed(5)},${lat.toFixed(5)}`).join(';')
}

export function clearDrivingRouteCache(): void {
  polylineCache.clear()
  inflight.clear()
}

export function readCachedDrivingRoute(
  coordinates: readonly LngLat[],
): GeoJSON.Feature<GeoJSON.LineString> | null {
  if (coordinates.length < 2) return null
  return polylineCache.get(drivingRouteCacheKey(coordinates)) ?? null
}

/**
 * Fetch the recommended driving geometry through `coordinates` (lng, lat).
 * Returns null when the token is missing, the request fails, or Mapbox
 * cannot snap the points onto a road — never throws.
 */
export async function fetchMapboxDrivingRoute(
  coordinates: readonly LngLat[],
): Promise<GeoJSON.Feature<GeoJSON.LineString> | null> {
  const waypoints = sanitizeWaypoints(coordinates)
  if (waypoints.length < 2) return null

  const key = drivingRouteCacheKey(waypoints)
  const cached = polylineCache.get(key)
  if (cached) return cached

  const pending = inflight.get(key)
  if (pending) return pending

  const request = requestDrivingRoute(waypoints)
    .then((feature) => {
      if (feature) polylineCache.set(key, feature)
      return feature
    })
    .finally(() => {
      inflight.delete(key)
    })
  inflight.set(key, request)
  return request
}

async function requestDrivingRoute(
  waypoints: LngLat[],
): Promise<GeoJSON.Feature<GeoJSON.LineString> | null> {
  const token = process.env.EXPO_PUBLIC_MAPBOX_TOKEN || env.EXPO_PUBLIC_MAPBOX_TOKEN || ''
  if (!token) return null

  try {
    if (waypoints.length <= MAX_WAYPOINTS) {
      return await requestDrivingLeg(waypoints, token)
    }

    const chunks: GeoJSON.Position[] = []
    for (let start = 0; start < waypoints.length - 1; start += MAX_WAYPOINTS - 1) {
      const leg = waypoints.slice(start, start + MAX_WAYPOINTS)
      const feature = await requestDrivingLeg(leg, token)
      if (!feature) return null
      const next = feature.geometry.coordinates
      if (chunks.length > 0 && next.length > 0) chunks.pop()
      chunks.push(...next)
    }
    return chunks.length >= 2 ? lineFeature(chunks) : null
  } catch {
    return null
  }
}

async function requestDrivingLeg(
  waypoints: LngLat[],
  token: string,
): Promise<GeoJSON.Feature<GeoJSON.LineString> | null> {
  const path = waypoints.map(([lng, lat]) => `${lng},${lat}`).join(';')
  const params = new URLSearchParams({
    geometries: 'geojson',
    overview: 'full',
    alternatives: 'false',
    steps: 'false',
    language: 'es',
    access_token: token,
  })
  const response = await fetchWithTimeout(
    `${DIRECTIONS_URL}/${path}?${params.toString()}`,
    undefined,
    { timeoutMs: DIRECTIONS_TIMEOUT_MS, retry: true },
  )
  if (!response.ok) return null
  const payload = (await response.json()) as {
    code?: unknown
    routes?: Array<{ geometry?: unknown }>
  }
  if (payload.code !== 'Ok' || !Array.isArray(payload.routes) || !payload.routes[0]) {
    return null
  }
  return readLineFeature(payload.routes[0].geometry)
}

function sanitizeWaypoints(coordinates: readonly LngLat[]): LngLat[] {
  const cleaned: LngLat[] = []
  for (const coordinate of coordinates) {
    const lng = Number(coordinate[0])
    const lat = Number(coordinate[1])
    if (
      !Number.isFinite(lng) ||
      !Number.isFinite(lat) ||
      Math.abs(lng) > 180 ||
      Math.abs(lat) > 90
    ) {
      continue
    }
    const previous = cleaned[cleaned.length - 1]
    if (previous && previous[0] === lng && previous[1] === lat) continue
    cleaned.push([lng, lat])
  }
  return cleaned
}

function readLineFeature(value: unknown): GeoJSON.Feature<GeoJSON.LineString> | null {
  if (!value || typeof value !== 'object') return null
  const geometry = value as { type?: unknown; coordinates?: unknown }
  const coordinates = readCoordinateArray(geometry.coordinates)
  if (geometry.type !== 'LineString' || coordinates.length < 2) return null
  return lineFeature(coordinates)
}

function readCoordinateArray(value: unknown): GeoJSON.Position[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((coordinate) => {
    if (!Array.isArray(coordinate) || coordinate.length < 2) return []
    const longitude = Number(coordinate[0])
    const latitude = Number(coordinate[1])
    return Number.isFinite(longitude) &&
      Number.isFinite(latitude) &&
      Math.abs(longitude) <= 180 &&
      Math.abs(latitude) <= 90
      ? ([[longitude, latitude]] as GeoJSON.Position[])
      : []
  })
}

function lineFeature(coordinates: GeoJSON.Position[]): GeoJSON.Feature<GeoJSON.LineString> {
  return {
    type: 'Feature',
    properties: { source: 'mapbox-directions' },
    geometry: { type: 'LineString', coordinates },
  }
}
